/* eslint-disable react/jsx-props-no-spreading */
import { Input as AntInput } from 'antd'
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  LoadingOutlined,
} from '@ant-design/icons'
import { useField, useFormikContext } from 'formik'
import { useCallback, useRef, useState } from 'react'
import { useTheme } from 'react-jss'
import PropTypes from 'prop-types'

import api from '../../api'
import Item from '../Forms/Item'

const INTEGRATION_NAME_FORMAT = /^[a-z0-9_]+$/
const isIntegrationNameValid = value =>
  INTEGRATION_NAME_FORMAT.test(value || '')

const FORMAT_ERROR_MESSAGE =
  'Only lowercase letters, numbers, and underscores are allowed'

export default function IntegrationNameField({
  name = 'integrationName',
  initialValue = undefined,
  label = 'Integration Name',
  placeholder = 'integration_name',
  onUserEdit = undefined,
}) {
  const { brandSuccess, brandDanger } = useTheme()
  const { isValidating } = useFormikContext()
  const [lastVerified, setLastVerified] = useState(null)
  const cacheRef = useRef(new Map())
  const latestValueRef = useRef(null)

  const locked = Boolean(initialValue?.trim())

  const validate = useCallback(
    async value => {
      if (locked) return undefined
      if (!value) return undefined
      if (!isIntegrationNameValid(value)) return FORMAT_ERROR_MESSAGE
      latestValueRef.current = value
      const cache = cacheRef.current
      if (!cache.has(value)) {
        cache.set(
          value,
          api.practices
            .checkIntegrationName(value)
            .catch(() => ({ available: true })),
        )
      }
      const result = await cache.get(value)
      // Guard against out-of-order resolves: a stale promise must not
      // clobber lastVerified, or the state machine gets stuck in 'checking'.
      if (latestValueRef.current === value) setLastVerified(value)
      if (result.available) return undefined
      return result.message || 'This integration name is already taken'
    },
    [locked],
  )

  const [field, meta] = useField({ name, validate })

  const state = (() => {
    if (locked) return 'locked'
    if (!field.value) return 'empty'
    if (!isIntegrationNameValid(field.value)) return 'invalid'
    if (isValidating || lastVerified !== field.value) return 'checking'
    return meta.error ? 'taken' : 'available'
  })()

  const suffix = {
    locked: <span />,
    empty: <span />,
    invalid: <CloseCircleOutlined style={{ color: brandDanger }} />,
    checking: <LoadingOutlined spin />,
    taken: <CloseCircleOutlined style={{ color: brandDanger }} />,
    available: <CheckCircleOutlined style={{ color: brandSuccess }} />,
  }[state]

  const showError = state === 'invalid' || state === 'taken'
  const id = `practice-form-${name}`

  return (
    <Item
      label={label}
      htmlFor={id}
      validateStatus={showError ? 'error' : ''}
      help={showError && meta.error ? meta.error : undefined}
      style={showError ? { marginBottom: 48 } : undefined}
    >
      <AntInput
        id={id}
        {...field}
        onChange={e => {
          field.onChange(e)
          onUserEdit?.(e.target.value)
        }}
        placeholder={placeholder}
        disabled={locked}
        suffix={suffix}
      />
    </Item>
  )
}

IntegrationNameField.propTypes = {
  name: PropTypes.string,
  initialValue: PropTypes.string,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  onUserEdit: PropTypes.func,
}
